import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, CircularProgress, TextField, Typography } from '@material-ui/core'
import { Info as InfoIcon } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import clsx from 'clsx'
import { Formik, useFormikContext } from 'formik'
import moment from 'moment'
import { CountriesProxy, EventsProxy } from 'services'
import { SectionLayoutDialog } from 'shared'
import { loadTicketTypeFees, updateEventMercardoPagoAuthorization } from 'state/modules/events'
import { ARGENTINA_COUNTRY, STRING_EMPTY } from 'utils/constants'
import { calculatePercentage, getFeeAmount, mercadoPagoCalculateFinalCost } from 'utils/functions'
import { showSnackbarSuccess } from 'utils/snackbar'
import * as Yup from 'yup'

import { useStyles } from './EditEventMercadoPagoAccountDialog.style'

const DIALOG_STATES = {
  FORM_DIALOG: 'FORM_DIALOG',
  UPDATE_TICKET_NOTIFICATION_DIALOG: 'UPDATE_TICKET_NOTIFICATION_DIALOG'
}

const EditEventMercadoPagoAccountDialog = ({ open, onClose }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { mercadoPagoAuthorization, event, ticketTypeFees } = useSelector((state) => state.events)

  const { values: formState, setFieldValue } = useFormikContext()

  const [provinces, setProvinces] = useState([])
  const [dialogState, setDialogState] = useState(DIALOG_STATES.FORM_DIALOG)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getAreaLevel1(ARGENTINA_COUNTRY.value)
  }, [])

  const handleOnClose = () => {
    setDialogState(DIALOG_STATES.FORM_DIALOG)
    onClose()
  }

  const values = {
    username: mercadoPagoAuthorization?.mpUserName || STRING_EMPTY,
    idNumber: mercadoPagoAuthorization?.mpIdNumber || STRING_EMPTY,
    country: mercadoPagoAuthorization?.countryId
      ? {
          value: mercadoPagoAuthorization.countryId,
          label: mercadoPagoAuthorization.countryName || STRING_EMPTY
        }
      : ARGENTINA_COUNTRY,
    areaLevel1: mercadoPagoAuthorization?.areaLevel1Id
      ? {
          value: mercadoPagoAuthorization.areaLevel1Id,
          label: mercadoPagoAuthorization.areaLevel1Name || STRING_EMPTY
        }
      : null,
    areaLevel1Text: STRING_EMPTY
  }
  const validationSchema = Yup.object().shape({})

  const onSubmit = async (values) => {
    setLoading(true)

    const payload = {
      mpUserName: values.username,
      mpIdNumber: values.idNumber,
      countryId: values.country?.value,
      areaLevel1Id: values.areaLevel1?.value
    }

    const bindingTickets = formState.ticketTypes.filter(
      ({ mercadoPagoEnabled, isActive, saleTo }) =>
        mercadoPagoEnabled && isActive && moment().isBefore(saleTo)
    )

    const showNotification =
      dialogState === DIALOG_STATES.FORM_DIALOG &&
      mercadoPagoAuthorization.areaLevel1Id !== payload.areaLevel1Id &&
      !!bindingTickets.length

    if (showNotification) {
      setDialogState(DIALOG_STATES.UPDATE_TICKET_NOTIFICATION_DIALOG)
      setLoading(false)
      return
    }

    const data = await dispatch(updateEventMercardoPagoAuthorization(event.id, payload))

    if (data) {
      const proxy = new EventsProxy()
      const ticketTypeCurrentFees = await proxy.getTicketTypeFees(payload.areaLevel1Id)

      const updateTicketTypes = [
        ...formState.ticketTypes.filter((x) => !x.mercadoPagoEnabled || moment().isAfter(x.saleTo)),
        ...bindingTickets.map((ticket) => {
          const currentFee = ticketTypeFees.find(
            (fee) => Number(fee.feePercentage) === Number(ticket.mercadoPagoFee)
          )

          const toUpdateFee = ticketTypeCurrentFees.find((fee) => fee.name === currentFee.name)

          const finalCost =
            ticket.price === ticket.mercadoPagoPrice
              ? ticket.mercadoPagoPrice
              : mercadoPagoCalculateFinalCost(
                  ticket.price,
                  calculatePercentage(ticket.sportmetricFeePercentage),
                  calculatePercentage(toUpdateFee.feePercentage)
                )

          return {
            ...ticket,
            hasChanges: true,
            mercadoPagoFee: toUpdateFee.feePercentage,
            mercadoPagoPrice: finalCost,
            sportmetricFeeAmount: getFeeAmount(finalCost, ticket.sportmetricFeePercentage, 2)
          }
        })
      ]

      await dispatch(loadTicketTypeFees(payload.areaLevel1Id))

      setFieldValue('ticketTypes', updateTicketTypes)

      handleOnClose()
      showSnackbarSuccess('¡Cuenta de Mercado Pago actualizada correctamente!')

      setLoading(false)
    }
  }

  const getAreaLevel1 = async (countryId) => {
    const proxy = new CountriesProxy()
    const areaLevel1List = await proxy.getAreaLevel1(countryId)

    if (areaLevel1List.length)
      setProvinces(
        areaLevel1List.map((x) => ({
          value: x.id,
          label: x.name
        }))
      )
  }

  return (
    <SectionLayoutDialog
      title='Editar cuenta MP'
      open={open}
      onClose={handleOnClose}
      className={classes.dialog}>
      <Formik
        initialValues={values}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={onSubmit}>
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          errors,
          touched,
          isValid,
          isSubmitting
        }) => (
          <form onSubmit={handleSubmit} className={classes.form}>
            {dialogState === DIALOG_STATES.FORM_DIALOG && (
              <div className={classes.block}>
                <TextField
                  className={classes.textField}
                  variant='outlined'
                  value={values.username}
                  label='Nombre de usuario MP'
                  name='username'
                  disabled
                  onChange={handleChange}
                  onBlur={handleBlur}
                  size='small'
                />
                <TextField
                  className={classes.textField}
                  variant='outlined'
                  value={values.idNumber}
                  label='DNI/CUIL/CUIT'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name='idNumber'
                  disabled
                  size='small'
                />

                <Autocomplete
                  id='number-autocomplete'
                  options={[ARGENTINA_COUNTRY]}
                  getOptionLabel={(option) => option.label || STRING_EMPTY}
                  selectOnFocus
                  filterOptions={(x) => x}
                  className={clsx(classes.combo, 'disabled')}
                  value={values.country}
                  loadingText={<Typography align='center'> Cargando...</Typography>}
                  noOptionsText={<Typography align='center'> Sin opciones</Typography>}
                  clearText='Quitar todo'
                  openText='Abrir desplegable'
                  disableClearable
                  disabled={mercadoPagoAuthorization.country}
                  onChange={(event, optionSelected) => setFieldValue('country', optionSelected)}
                  closeText='Cerrar desplegable'
                  classes={{
                    option: classes.option
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className={classes.textField}
                      name='country'
                      label='País'
                      variant='outlined'
                      error={touched.country && Boolean(errors.country)}
                      helperText={errors.country}
                      onBlur={handleBlur}
                    />
                  )}
                />

                <div>
                  <Autocomplete
                    id='number-autocomplete'
                    options={provinces}
                    getOptionLabel={(option) => option.label || STRING_EMPTY}
                    selectOnFocus
                    onInputChange={(_, value) => setFieldValue('areaLevel1Text', value)}
                    onChange={(event, optionSelected) =>
                      setFieldValue('areaLevel1', optionSelected)
                    }
                    filterOptions={(x) =>
                      x.filter((option) =>
                        option.label
                          .toLowerCase()
                          .trim()
                          .includes(values.areaLevel1Text.toLowerCase().trim())
                      )
                    }
                    value={values.areaLevel1}
                    loadingText={<Typography align='center'> Cargando...</Typography>}
                    noOptionsText={<Typography align='center'> Sin opciones</Typography>}
                    clearText='Quitar todo'
                    openText='Abrir desplegable'
                    className={classes.combo}
                    disableClearable
                    closeText='Cerrar desplegable'
                    classes={{
                      option: classes.option
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className={classes.textField}
                        name='areaLevel1'
                        label='Provincia'
                        variant='outlined'
                        error={touched.areaLevel1 && Boolean(errors.areaLevel1)}
                        helperText={errors.areaLevel1}
                        onBlur={handleBlur}
                      />
                    )}
                  />

                  <div className={classes.infoText}>
                    <InfoIcon color='primary' />
                    La provincia que elijas tiene que coincidir con la que figura en tu cuenta de
                    Mercado Pago.
                  </div>
                </div>
              </div>
            )}

            {dialogState === DIALOG_STATES.UPDATE_TICKET_NOTIFICATION_DIALOG && (
              <div className={classes.notificationBlock}>
                <Typography variant='h6' align='center'>
                  Para aplicar los cambios en la cuenta de Mercado Pago, es necesario actualizar los
                  tickets del evento.
                </Typography>
                <Typography variant='h6' align='center'>
                  Si no acepta, no se realizarán modificaciones.
                </Typography>
              </div>
            )}

            <div className={classes.buttonContainer}>
              <Button
                color='primary'
                variant='contained'
                className={classes.saveButton}
                disabled={!isValid || loading || isSubmitting}
                endIcon={loading && <CircularProgress size={16} color='primary' />}
                type='submit'>
                {dialogState === DIALOG_STATES.FORM_DIALOG ? 'Guardar cambios' : 'Aceptar'}
              </Button>
              {dialogState === DIALOG_STATES.UPDATE_TICKET_NOTIFICATION_DIALOG && (
                <Button
                  color='primary'
                  variant='outlined'
                  className={classes.saveButton}
                  disabled={isSubmitting}
                  onClick={() => setDialogState(DIALOG_STATES.FORM_DIALOG)}>
                  Cancelar
                </Button>
              )}
            </div>
          </form>
        )}
      </Formik>
    </SectionLayoutDialog>
  )
}

export default EditEventMercadoPagoAccountDialog
